import { useCallback, useEffect, useState } from 'react'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

const STORAGE_KEY_PREFIX = 'whats_new_banner_dismissed'
const RESHOW_AFTER_DAYS = 7

function getIsDismissed(workspaceId: number | undefined): boolean {
  if (typeof window === 'undefined' || !workspaceId) return false
  const storageKey = `${STORAGE_KEY_PREFIX}_${workspaceId}`
  const stored = localStorage.getItem(storageKey)
  if (!stored) return false
  const dismissedAt = parseInt(stored, 10)
  if (Number.isNaN(dismissedAt)) return false
  const daysSinceDismissed = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24)
  return daysSinceDismissed < RESHOW_AFTER_DAYS
}

/**
 * Hook to manage visibility of the What's New banner.
 * When dismissed, stores timestamp in localStorage. Banner shows again after RESHOW_AFTER_DAYS.
 */
export function useWhatsNewBannerVisibility() {
  const workspaceId = useCurrentWorkspaceId()
  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    setIsDismissed(getIsDismissed(workspaceId))
  }, [workspaceId])

  const dismiss = useCallback(() => {
    if (!workspaceId) return
    const storageKey = `${STORAGE_KEY_PREFIX}_${workspaceId}`
    localStorage.setItem(storageKey, Date.now().toString())
    setIsDismissed(true)
  }, [workspaceId])

  return { isVisible: !isDismissed, dismiss }
}
